import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import Footer from "../components/Footer";

/* ----------------------- categories ----------------------- */
const CATEGORIES = ["Emissions", "Performance", "Data"];

/* ----------------------- data ----------------------- */
export const tools = [
  {
    title: "IRA 45V Hydrogen Tax Credit Estimator",
    href: "/interactive-tools/45v-credit-estimator",
    summary: "Estimate your Section 45V clean hydrogen PTC by tier, compare PTC vs ITC, and model prevailing wage impact.",
    badge: "New",
    image: "/tools/45v-estimator.jpg",
    alt: "Calculator estimating IRA Section 45V hydrogen production tax credit tiers and values.",
    categories: ["Data", "Performance"]
  },
  {
    title: "Gas Turbine Model Comparison",
    href: "/interactive-tools/turbine-comparison",
    summary: "Compare 30+ gas turbine models from GE Vernova, Siemens Energy, Mitsubishi Power, Ansaldo & Kawasaki. Filter by power, H₂ capability, efficiency. Side-by-side radar chart.",
    badge: "New",
    image: "/tools/turbine-comparison.jpg",
    alt: "Side-by-side comparison of gas turbine models from major OEMs with performance radar chart.",
    categories: ["Performance", "Data"]
  },
  {
    title: "Hydrogen Fuel Switching Breakeven Calculator",
    href: "/interactive-tools/h2-breakeven",
    summary: "Find the hydrogen price at which fuel switching beats natural gas. Model carbon pricing, 45V tax credits, turbine efficiency, and capacity factor.",
    badge: "New",
    image: "/tools/h2-breakeven.jpg",
    alt: "Chart showing hydrogen vs natural gas fuel cost crossover with carbon price and tax credit sensitivity.",
    categories: ["Performance", "Data"]
  },
  {
    title: "Data Center Power Architecture Sizer",
    href: "/interactive-tools/data-center-power-sizer",
    summary: "Size behind-the-meter gas turbine, hybrid, and bridging storage architecture for data center campuses.",
    badge: "New",
    image: "/tools/data-center-sizer.jpg",
    alt: "Executive calculator sizing behind-the-meter gas turbine, hybrid, and BESS architecture for data center campuses.",
    categories: ["Performance", "Data"]
  },
  {
    title: "Carbon Price Impact Modeler",
    href: "/interactive-tools/carbon-price-impact-modeler",
    summary: "Model how EU ETS, UK ETS, custom carbon prices, and CBAM-style exposure affect gas turbine power costs through 2040.",
    badge: "New",
    image: "/tools/carbon-price-impact-for-gas-turbines.jpg",
    alt: "Interactive model showing carbon price pathways, fuel comparison, and carbon cost of electricity for gas turbines.",
    categories: ["Emissions", "Performance", "Data"]
  },
  {
    title: "Hydrogen Blend Dispatch & Emissions Optimizer",
    href: "/interactive-tools/hydrogen-blend-dispatch-optimizer",
    summary: "Model how hydrogen blending changes gas turbine fuel burn, emissions, carbon cost, and dispatch economics.",
    badge: "New",
    image: "/tools/hydrogen-blend-optimizer.jpg",
    alt: "Premium optimizer showing hydrogen blend effects on gas turbine variable cost, emissions, and dispatch screening.",
    categories: ["Emissions", "Performance", "Data"]
  },
  {
    title: "Gas Turbine Efficiency Map",
    href: "/interactive-tools/gas-turbine-efficiency-map",
    summary: "Map turbine efficiency, heat rate, output, and emissions under ambient, load, hydrogen blend, and CCS scenarios.",
    badge: "New",
    image: "/tools/gas-turbine-efficiency-map.jpg",
    alt: "Performance mapping tool showing gas turbine efficiency, heat rate, output, and emissions under site-condition and decarbonisation scenarios.",
    categories: ["Performance", "Emissions", "Data"]
  },
  {
    title: "Turbine Inlet Cooling ROI Calculator",
    href: "/interactive-tools/turbine-inlet-cooling-roi-calculator",
    summary: "Compare evaporative cooling, fogging, chilling, and TES using real weather patterns, net uplift, and payback.",
    badge: "New",
    image: "/tools/turbine-cooling-dashboard.jpg",
    alt: "Weather-aware turbine inlet cooling ROI tool comparing evaporative cooling, fogging, mechanical chilling, and TES.",
    categories: ["Performance", "Data", "Emissions"]
  },
  {
    title: "Emissions Units + O₂ Reference Converter",
    href: "/interactive-tools/emissions-unit-converter",
    summary: "Convert ppm to mg/Nm³, correct O₂ reference (15%) and adjust for moisture.",
    image: "/tools/emissions-dashboard-o2.png",
    alt: "Dashboard for converting emissions units and correcting O2 reference.",
    categories: ["Emissions", "Data"]
  },
  {
    title: "Flight Carbon Calculator",
    href: "/interactive-tools/flight-carbon-calculator",
    summary: "Calculate your flight's CO₂ emissions and discover the connection between jet engines and gas turbines.",
    image: "/tools/flight-carbon-calculator.jpg",
    alt: "Flight carbon footprint calculator showing emissions for air travel routes.",
    categories: ["Emissions", "Data"]
  },
  {
    title: "H₂ Material & Lifetime Predictor",
    href: "/interactive-tools/h2-material-predictor",
    summary: "Assess hydrogen embrittlement risk and predict component lifetime for materials in H₂ service.",
    image: "/tools/h2-material.webp",
    alt: "Material compatibility assessment tool showing risk scores for hydrogen service components.",
    categories: ["Performance", "Data"]
  },
  {
    title: "Hydrogen Purity vs. NOx Plotter",
    href: "/interactive-tools/h2-purity-nox-plotter",
    summary: "Compare NOx emissions and costs across different hydrogen sources and purity levels.",
    image: "/tools/h2-purity-nox.webp",
    alt: "Chart comparing NOx emissions for various hydrogen purity levels and sources.",
    categories: ["Emissions", "Performance"]
  },
  {
    title: "Turbine Reliability Benchmarker",
    href: "/interactive-tools/turbine-reliability-benchmarker",
    summary: "Benchmark your fleet's performance against industry standards and identify improvement opportunities.",
    image: "/tools/reliability.webp",
    alt: "Dashboard comparing turbine reliability metrics against industry benchmarks.",
    categories: ["Performance", "Data"]
  },
  {
    title: "Diluent Trade-off Calculator",
    href: "/interactive-tools/diluent-calculator",
    summary: "Compare nitrogen vs. steam injection for NOx control with efficiency and cost analysis.",
    image: "/tools/diluent.webp",
    alt: "Comparison tool for nitrogen and steam injection strategies in hydrogen combustion.",
    categories: ["Emissions", "Performance"]
  },
  {
    title: "Eco Savings Calculator",
    href: "/interactive-tools/eco-savings-calculator",
    summary:
      "Carbon footprint & solar/EV savings (UK, US, South Africa). Share results, export JSON, and see credit progress.",
    image: "/tools/eco-savings.jpg",
    alt: "Eco Savings Calculator with KPIs for solar, EV and email emissions plus charts.",
    categories: ["Emissions", "Data"]
  },
  {
    title: "Hydrogen Blend Calculator",
    href: "/interactive-tools/hydrogen-blend-calculator",
    summary: "Slide %H₂ to see illustrative effects on LHV, NOₓ index and CO₂e reduction.",
    image: "/tools/hydrogen-blend-calculator.webp",
    alt: "Interactive chart showing the effect of hydrogen blending on gas turbine emissions and heating value.",
    categories: ["Emissions", "Performance"],
  },
  {
    title: "Turbine Telemetry Simulator",
    href: "/interactive-tools/telemetry-simulator",
    summary: "Emit realistic turbine tags via WebSocket/MQTT for dashboards and tests.",
    image: "/tools/telementry-sim.webp",
    alt: "Web-based simulator emitting synthetic gas turbine telemetry via WebSocket or MQTT.",
    categories: ["Data", "Performance"],
  },
  {
    title: "SCADA Dashboard Demo",
    href: "/interactive-tools/scada-dashboard",
    summary: "Real-time KPIs and trend plots for turbine operation.",
    image: "/tools/scada-demo.webp",
    alt: "Live SCADA-style dashboard with KPIs and trend charts for gas turbine operation.",
    categories: ["Data", "Performance"],
  },
  {
    title: "LCOE & Retrofit TCO",
    href: "/interactive-tools/lcoe-retrofit",
    summary: "Compare levelized costs and retrofit scenarios with carbon pricing.",
    image: "/tools/lcoe.webp",
    alt: "Comparison charts for levelized cost of energy and retrofit total cost scenarios.",
    categories: ["Performance", "Data"],
  },
  {
    title: "Emissions Estimator",
    href: "/interactive-tools/emissions-estimator",
    summary: "Quick CO₂e and NOₓ estimates across fuels and duty cycles.",
    image: "/tools/emissions.webp",
    alt: "Calculator estimating CO₂e and NOₓ emissions for different fuels and duty cycles.",
    categories: ["Emissions"],
  },
  {
    title: "Combustion Properties Explorer",
    href: "/interactive-tools/combustion-explorer",
    summary: "Fuel LHV, stoichiometry and flame-speed primers with charts.",
    image: "/tools/combustion-explorer.webp",
    alt: "Interactive charts of fuel lower heating value, stoichiometry, and laminar flame speed.",
    categories: ["Performance", "Data"],
  },
  {
    title: "Grid Peaker Scheduler",
    href: "/interactive-tools/peaker-scheduler",
    summary: "Dispatch a turbine with renewables and storage in a toy optimizer.",
    image: "/tools/grid-peaker-scheduler.webp",
    alt: "Toy optimizer scheduling a gas turbine alongside renewables and battery storage.",
    categories: ["Performance", "Data"],
  },
  {
    title: "H₂-Ready Plant Checklist",
    href: "/interactive-tools/h2-ready-checklist",
    summary: "Readiness templates for fuel, purge, fire detection and controls.",
    image: "/tools/h2-ready-checklist.webp",
    alt: "Readiness checklist for hydrogen fuel systems, purge, fire detection, and controls.",
    categories: ["Performance"],
  },
  {
    title: "Data Center Reliability Scenarios",
    href: "/interactive-tools/datacenter-scenarios",
    summary: "N+1, firming and curtailment scenarios.",
    image: "/tools/datacenter.webp",
    alt: "Scenarios exploring N+1 reliability, firming, and curtailment for data center power.",
    categories: ["Performance", "Data"],
  },
  {
    title: "Alarm Rule Sandbox",
    href: "/interactive-tools/alarm-rules",
    summary: "Try thresholds and deadbands on sample signals.",
    image: "/tools/alarm.webp",
    alt: "Interactive tuning of alarm thresholds and deadbands on sample turbine signals.",
    categories: ["Data", "Performance"],
  },
  {
    title: "Anomaly Detection Playground",
    href: "/interactive-tools/anomaly-playground",
    summary: "Simple analytics on vibration, EGT and RPM.",
    image: "/tools/anomaly-detection-playground.webp",
    alt: "Simple anomaly detection on vibration, exhaust gas temperature and RPM signals.",
    categories: ["Data", "Performance"],
  },
  {
    title: "Tag Mapper",
    href: "/interactive-tools/tag-mapper",
    summary: "Normalize OPC UA/Modbus tags to a clean schema.",
    image: "/tools/tag-mapper.webp",
    alt: "Tool to normalize OPC UA and Modbus tags into a unified schema.",
    categories: ["Data"],
  },
];

const faq = [
  { q: "Are these results suitable for engineering design?", a: "No. The demos are illustrative. Replace formulas with validated models for engineering decisions." },
  { q: "Can I integrate the tools into my site or portal?", a: "Yes. Each tool can be embedded or linked. The dashboards accept WebSocket/MQTT streams." },
  { q: "What browsers are supported?", a: "We target current Chrome, Edge, Firefox and Safari releases." },
  { q: "Do you offer custom models and retrofits?", a: "We can tailor models, dashboards and checklists to your turbine and fuel mix." },
];

/* --------------------- error boundary --------------------- */
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, msg: "" }; }
  static getDerivedStateFromError(err) { return { hasError: true, msg: err?.message || "Unknown error" }; }
  componentDidCatch(err, info) { console.error("InteractiveTools crashed:", err, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="mx-auto mt-10 max-w-3xl rounded-xl border p-6">
          <h2 className="text-xl font-semibold">Something went wrong.</h2>
          <p className="mt-2 text-sm opacity-90">{this.state.msg}</p>
          <p className="mt-2 text-sm opacity-90">
            Try refreshing, or go back to the <a className="text-emerald-300 underline" href="/#/">home page</a>.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ----------------------- components ----------------------- */
export function ToolCard({ t }) {
  const imgSrc = t.image || "/tools/placeholder-tool.webp";
  const alt = t.alt || `Illustration of ${t.title} tool for gas turbines.`;
  return (
    <article className="group relative overflow-hidden rounded-2xl border border-emerald-800/40 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <Link to={t.href} className="absolute inset-0 z-10" aria-label={`Open ${t.title}`} />
      <figure className="relative">
        <img
          src={imgSrc}
          alt={alt}
          className="aspect-video w-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
          decoding="async"
          onError={(e) => { e.currentTarget.src = "/tools/placeholder-tool.webp"; }}
        />
        <figcaption className="sr-only">{alt}</figcaption>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-black/0 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        {t.badge && (
          <span className="absolute right-3 top-3 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
            {t.badge}
          </span>
        )}
      </figure>
      <header className="p-5">
        <h3 className="text-lg font-semibold text-secondary-900">{t.title}</h3>
        <p className="mt-1 text-sm text-secondary-600">{t.summary}</p>
        <div className="mt-4 text-sm font-medium text-emerald-700">Open ↗</div>
      </header>
    </article>
  );
}

export default function InteractiveTools() {
  const [query, setQuery] = useState("");
  const [selectedCats, setSelectedCats] = useState(new Set());

  const toggleCat = (cat) => {
    setSelectedCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const faqJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  }), []);

  const itemListSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Interactive Gas Turbine & Hydrogen Calculators",
    description: "Free engineering tools for hydrogen blend analysis, emissions estimation, telemetry simulation, and retrofit economics.",
    numberOfItems: tools.length,
    itemListElement: tools.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://www.greengasturbines.com${t.href}`,
      name: t.title
    }))
  }), []);

  const filteredTools = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tools.filter((t) => {
      const matchesText =
        !q ||
        t.title.toLowerCase().includes(q) ||
        t.summary.toLowerCase().includes(q);
      const matchesCats =
        selectedCats.size === 0 ||
        t.categories?.some((c) => selectedCats.has(c));
      return matchesText && matchesCats;
    });
  }, [query, selectedCats]);

  return (
    <div className="min-h-screen bg-emerald-950 text-emerald-50">
      <Helmet>
        <title>Free Gas Turbine & Hydrogen Calculators | GreenGasTurbines</title>
        <meta
          name="description"
          content="Free interactive tools for the energy sector. Calculate hydrogen blend effects, simulate turbine telemetry, estimate emissions, and model LCOE for your gas turbine projects."
        />
        <link rel="canonical" href="https://www.greengasturbines.com/interactive-tools" />
        <meta property="og:title" content="Free Gas Turbine & Hydrogen Calculators | GreenGasTurbines" />
        <meta property="og:description" content="Explore free interactive calculators for the energy sector. Model hydrogen blending, estimate turbine emissions (CO₂, NOₓ), simulate telemetry, and analyze the levelized cost of energy (LCOE)." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.greengasturbines.com/interactive-tools" />
        <meta property="og:image" content="https://www.greengasturbines.com/interactive-tools.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Preview of interactive gas turbine and hydrogen calculators." />
        <meta property="og:site_name" content="Green Gas Turbines" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@GreenGasTs" />
        <meta name="twitter:title" content="Free Gas Turbine & Hydrogen Calculators | GreenGasTurbines" />
        <meta name="twitter:description" content="Explore free interactive calculators for hydrogen-ready gas turbines, emissions, telemetry and LCOE analysis." />
        <meta name="twitter:image" content="https://www.greengasturbines.com/interactive-tools.jpg" />
        <meta name="twitter:image:alt" content="Preview of interactive gas turbine and hydrogen calculators." />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      </Helmet>

      <Header />

      <main id="main" className="pt-20">
        <ErrorBoundary>
          <section className="mx-auto max-w-6xl px-6 py-10" aria-labelledby="tools-heading">
            <header className="mb-8">
              <h1 id="tools-heading" className="text-3xl font-bold text-white">Interactive Tools</h1>
              <p className="mt-2 max-w-3xl text-emerald-100">
                Hands-on demos for hydrogen-ready gas turbines. Explore blends, emissions,
                telemetry and visualization — see what’s possible before you retrofit or deploy.
              </p>
            </header>

            {/* Search & Filters */}
            <section
              className="mb-6 rounded-2xl border border-emerald-800 bg-emerald-900/30 p-4"
              aria-labelledby="filter-heading"
            >
              <h2 id="filter-heading" className="sr-only">Search and filter tools</h2>
              <form
                role="search"
                aria-label="Search tools"
                className="flex flex-col gap-3 md:flex-row md:items-center"
                onSubmit={(e) => e.preventDefault()}
              >
                <label htmlFor="tool-search" className="sr-only">Search tools</label>
                <div className="relative md:flex-1">
                  <input
                    id="tool-search"
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search tools by title or summary…"
                    className="w-full rounded-xl border border-emerald-800 bg-emerald-900/60 p-3 text-emerald-50 placeholder-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    aria-describedby="results-count"
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={() => setQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md border border-emerald-700 px-2 py-1 text-sm text-emerald-200 hover:bg-emerald-800"
                      aria-label="Clear search"
                    >
                      Clear
                    </button>
                  )}
                </div>

                <nav aria-label="Filter by category" className="flex flex-wrap gap-2">
                  {CATEGORIES.map((cat) => {
                    const active = selectedCats.has(cat);
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => toggleCat(cat)}
                        aria-pressed={active}
                        className={`rounded-full border px-3 py-1 text-sm ${active
                          ? "border-emerald-400 bg-emerald-400/10 text-emerald-200"
                          : "border-emerald-800 bg-emerald-900/40 text-emerald-300 hover:border-emerald-600"
                          }`}
                      >
                        {cat}
                      </button>
                    );
                  })}
                  {selectedCats.size > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedCats(new Set())}
                      className="rounded-full border border-emerald-800 px-3 py-1 text-sm text-emerald-300 hover:border-emerald-600"
                    >
                      Reset
                    </button>
                  )}
                </nav>
              </form>
              <p id="results-count" className="mt-2 text-sm text-emerald-300" aria-live="polite">
                Showing {filteredTools.length} of {tools.length} tools
              </p>
            </section>

            {/* Tools grid */}
            <section className="grid grid-cols-1 gap-6 md:grid-cols-3" aria-label="Tool list">
              {filteredTools.length > 0 ? (
                filteredTools.map((t) => <ToolCard key={t.title} t={t} />)
              ) : (
                <div className="col-span-full rounded-xl border border-emerald-800/60 bg-emerald-900/30 p-6 text-emerald-200">
                  No tools match your search or filters. Try clearing the filters.
                </div>
              )}
            </section>

            {/* Content sections */}
            <section className="mt-16 max-w-3xl text-emerald-100" aria-labelledby="benefits-heading">
              <h2 id="benefits-heading" className="mb-2 text-2xl font-semibold">Why Use Our Free Engineering Calculators?</h2>
              <p className="mb-3">
                Our calculators and demos help engineers, project managers, and technicians to quickly estimate the impact of operational changes, prototype data flows, and visualize performance improvements.
              </p>
              <h3 className="mb-2 text-xl font-semibold">Key Applications & Benefits</h3>
              <ul className="list-disc pl-5 marker:text-emerald-400">
                <li>Model illustrative CO₂e savings from hydrogen blends.</li>
                <li>Stream synthetic turbine data into dashboards and rules.</li>
                <li>Compare retrofit pathways and OPEX trade-offs.</li>
              </ul>
            </section>

            <section className="mt-12" aria-labelledby="faq-heading">
              <h2 id="faq-heading" className="mb-4 text-2xl font-semibold">Frequently Asked Questions</h2>
              <div className="divide-y divide-emerald-800 rounded-2xl border border-emerald-800">
                {faq.map((f) => (
                  <details key={f.q} className="group p-5">
                    <summary className="cursor-pointer list-none font-medium text-emerald-50">{f.q}</summary>
                    <p className="mt-2 text-sm text-emerald-200">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          </section>
        </ErrorBoundary>
      </main>

      <Footer />
    </div>
  );
}
